import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Container,
  Dialog,
  Typography,
  DialogActions,
  CardMedia,
  DialogContent,
  DialogContentText,
  Grid,
  DialogTitle,
  Divider,
  Paper,
  Box,
} from '@mui/material';
import axios from 'axios';
import PropTypes from 'prop-types';

import { fNumber } from '../../../utils/formatNumber';
import SERVER_API_URL from '../../../config';
import DistrictCard from '../cards/DistrictCard';
import { Searchbar } from '../../../components/searchbar';

export default function District({ containerRef, setSelectedDistrict, setShow }) {
  const [openDialog, setOpenDialog] = useState(false);
  const [districtList, setDistrictList] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [query, setQuery] = useState('');
  const [oneDistrict, setOneDistrict] = useState();
  const [avatars, setAvatars] = useState({});

  const getAvatarById = useCallback(async (id) => {
    try {
      const response = await axios.get(`${SERVER_API_URL}/getDistrictAvatar/getAvatarById/${id}`, {
        responseType: 'blob',
      });
      if (response.status === 200) {
        return URL.createObjectURL(response.data);
      }
      throw new Error('Failed to fetch avatar');
    } catch (e) {
      console.error('Error fetching avatar:', e);
      throw e;
    }
  }, []);

  const getDistrictAvatars = useCallback(
    (filteredDistricts) => {
      const newAvatars = {};
      filteredDistricts.forEach(async (district) => {
        if (!avatars[district.id]) {
          try {
            const url = await getAvatarById(district.id);
            newAvatars[district.id] = url;
            setAvatars(newAvatars);
          } catch (error) {
            console.error('Error fetching avatar:', error);
          }
        }
      });
    },
    [getAvatarById, avatars]
  );

  useEffect(() => {
    const getAllDistrict = async () => {
      try {
        const response = await axios.get(`${SERVER_API_URL}/landing/getDistrictInfo`);
        if (response.status === 200) {
          setDistrictList(response.data);
          setDistricts(response.data);
        }
      } catch (error) {
        console.error(error);
      }
    };

    getAllDistrict();
  }, []);

  useEffect(() => {
    getDistrictAvatars(districts);
  }, [districts, getDistrictAvatars]);

  const twoLineNoWrapStyle = {
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    textOverflow: 'ellipsis',
    WebkitLineClamp: 2,
  };

  useEffect(() => {
    const all = districtList;
    setDistricts(query ? all.filter((district) => district.name.includes(query)) : all);
  }, [query, districtList]);

  const avatarsMemoized = useMemo(() => ({ ...avatars }), [avatars]);

  const handleDistrict = async (districtId) => {
    try {
      const response = await axios.get(`${SERVER_API_URL}/landing/getDistrictInfo/${districtId}`);

      if (response.status === 200) {
        setSelectedDistrict(response.data);
        if (containerRef.current) {
          containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setShow('church');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleDialog = (districtId) => {
    try {
      const filteredDistrict = districtList.find((district) => district.id === districtId);
      setOneDistrict(filteredDistrict);
      setOpenDialog(true);
    } catch (error) {
      console.error('Error handling dialog:', error);
    }
  };

  return (
    <>
      <Container sx={{ py: { xs: 6, sm: 10 } }} maxWidth="lg">
        <Typography variant="h3" sx={{ p: 2, justifyContent: 'center', justifySelf: 'center' }}>
          የወረዳ ዝርዝር
        </Typography>
        <Searchbar setQuery={setQuery} />
        <Grid container spacing={4}>
          {districts &&
            districts.map((district) => (
              <Grid item key={district.id} xs={12} sm={6} md={4}>
                <DistrictCard
                  district={district}
                  handleDialog={handleDialog}
                  handleDistrict={handleDistrict}
                  twoLineNoWrapStyle={twoLineNoWrapStyle}
                />
              </Grid>
            ))}
        </Grid>
        {(!districts || districts.length) === 0 && <Box sx={{ height: 200, p: 5, color: 'gray' }}>ውሂብ የለም።</Box>}
      </Container>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <Box sx={{ backgroundColor: (theme) => theme.palette.mode === 'light' ? '#F9F9FB' : '#272424' }}>
          <Paper sx={{ minWidth: 500, minHeight: 500 }}>
            {oneDistrict && avatarsMemoized && avatarsMemoized[oneDistrict.id] && (
              <CardMedia
                component="div"
                sx={{
                  // 16:9
                  pt: '56.25%',
                }}
                image={oneDistrict && avatarsMemoized && avatarsMemoized[oneDistrict.id]}
              />
            )}
            {oneDistrict && avatarsMemoized && !avatarsMemoized[oneDistrict.id] && (
              <CardMedia
                component="div"
                sx={{
                  // 16:9
                  pt: '56.25%',
                }}
                image="/assets/landing/empty_district.png"
              />
            )}
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography gutterBottom variant="h5" component="h2">
                  {oneDistrict && oneDistrict.name}
                </Typography>
                <Typography sx={{ minWidth: 100 }} gutterBottom variant="body1" color="darkgray">
                  የአጥቢያ ብዛት ፡ {fNumber(oneDistrict && oneDistrict.Church.length)}
                </Typography>
              </Box>{' '}
            </DialogTitle>
            <DialogContent>
              <DialogContentText> {oneDistrict && oneDistrict.description} </DialogContentText>
              <Divider sx={{ m: 2, fontSize: 24 }} />
              <DialogContentText> ታሪክ፡ {oneDistrict && oneDistrict.history}</DialogContentText>
              <DialogActions>
                <Button onClick={() => setOpenDialog(false)}>ተመለስ</Button>
              </DialogActions>
            </DialogContent>
          </Paper>
        </Box>
      </Dialog>
    </>
  );
}

District.propTypes = {
  setSelectedDistrict: PropTypes.func,
  setShow: PropTypes.func,
  containerRef: PropTypes.object,
};
